/* apps/web/src/branding/applyBranding.ts
 *
 * Writes Layer 1 of tokens.css from branding.config.ts at boot, and derives
 * the values a customer should not have to think about: the darker hover
 * shade, the tints, and — importantly — whether text on the brand color must
 * be white or near-black. Call once from main.tsx before render.
 */
import { branding, type Branding } from "./branding.config";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance. */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: string, b: string): number {
  const la = luminance(hexToRgb(a));
  const lb = luminance(hexToRgb(b));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Multiply toward black — the hover/pressed shade of any brand color. */
function darken(hex: string, amount = 0.22): string {
  const shaded = hexToRgb(hex).map((v) => Math.round(v * (1 - amount)));
  return "#" + shaded.map((v) => v.toString(16).padStart(2, "0")).join("");
}

function tint(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function applyBranding(b: Branding = branding): void {
  const root = document.documentElement;
  const set = (k: string, v: string): void => root.style.setProperty(k, v);

  set("--brand-primary", b.primaryColor);
  set("--brand-primary-strong", darken(b.primaryColor));
  set("--brand-secondary", b.secondaryColor);
  /* The Copilot spark tiles and the composer's tool badge sit ON the secondary
     tint, where the secondary itself is often too light to read. */
  set("--brand-secondary-strong", darken(b.secondaryColor, 0.28));
  set("--brand-primary-tint", tint(b.primaryColor, 0.06));
  set("--brand-primary-tint-strong", tint(b.primaryColor, 0.11));
  set("--brand-secondary-tint", tint(b.secondaryColor, 0.14));

  /* Never assume white text is readable on the configured brand color.
     A light brand (e.g. a yellow logo color) gets near-black label text. */
  const onBrand = contrast(b.primaryColor, "#ffffff") >= 4.5 ? "#ffffff" : "#141b26";
  set("--text-on-brand", onBrand);

  /* The same decision, expressed for full-colour artwork that sits on the
     brand colour (the Copilot mark on the login panel). Such a mark is a PNG,
     so it cannot take `color` — it is flattened with a filter instead:
     brightness(0) drives every pixel to black, and invert(1) lifts it to white
     when white is what the text beside it uses. Alpha is untouched either way,
     so the shape survives as a solid silhouette that matches its caption. */
  set("--brand-artwork-filter", onBrand === "#ffffff" ? "brightness(0) invert(1)" : "brightness(0)");

  /* A very light brand color would also fail as the focus ring on white.
     Fall back to the darker shade so keyboard focus stays visible. */
  const ring = contrast(b.secondaryColor, "#ffffff") >= 3 ? b.secondaryColor : darken(b.secondaryColor, 0.3);
  set("--focus-ring", ring);

  /* The tab carries the PRODUCT name, not the deployment name: the favicon
     beside it is the Copilot mark, so "Copilot" is what the two together read
     as. `brandName` still labels the login identity panel. */
  document.title = b.productName || b.brandName;
}
